/**
 * 전투 화면의 손가락 조작. 아래 명령 줄과, 전장의 손가락 자리에 뜨는 가상 조이스틱.
 *
 * 모바일에서 키보드 없이 한 판을 끝까지 할 수 있어야 한다. 그래서 버튼은 PC에서도
 * 보인다 — 같은 화면을 두 벌 만들면 한쪽이 낡는다. 조이스틱만 손가락 기기에서
 * 띄운다. 마우스로 조이스틱을 끄는 것은 WASD보다 불편하기만 하다.
 *
 * 여기서 만든 입력은 전부 `InputState`를 거친다. 키보드와 같은 길로 커맨드 로그에
 * 실려야 리플레이가 같은 판을 다시 만든다.
 */
import { ALL_SQUADS, MAX_SQUADS, Order } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { SKILLS, SKILL_CODE } from '../sim/skills.ts';
import type { InputState } from '../game/input.ts';
import { normalizeSelect } from '../game/loop.ts';
import { ALL_SQUADS_NAME, squadName } from './labels.ts';
import { SQUAD_COLORS } from '../render/atlas.ts';

/** 쓰는 부대를 모두 고른 선택인가(숫자키로 하나하나 고른 것도 전 부대다). */
function isAll(mask: number, used: ArrayLike<number>): boolean {
  for (let sq = 0; sq < MAX_SQUADS; sq++) if (used[sq] && (mask & (1 << sq)) === 0) return false;
  return true;
}

/**
 * 부대선택 버튼이 펼치는 목록(드롭업)의 선택지, 위에서 아래로: 전 부대, 내 부대, 부하 부대(쓰는 것만,
 * 번호 순). 부대가 하나뿐이면 전 부대가 곧 내 부대라 내 부대 하나다.
 *
 * 예전에는 누를 때마다 다음 부대로 돌고 빨리 두 번 누르면 전 부대였다. 원하는 부대까지 몇 번 눌러야
 * 하는지 세어야 했고, 두 번 누르기는 알려 주지 않으면 모른다 — 플레이한 사람이 "순회하지 말고
 * 드롭업 버튼들이 나와서 선택할 수 있게" 달라고 했다.
 */
export function selectionChoices(used: ArrayLike<number>): number[] {
  const subs: number[] = [];
  for (let sq = 1; sq < MAX_SQUADS; sq++) if (used[sq]) subs.push(1 << sq);
  return subs.length > 0 ? [ALL_SQUADS, 1, ...subs] : [1];
}

/** 선택 마스크를 사람이 읽는 이름으로. */
export function selectionName(mask: number, world: World): string {
  const used: number[] = [];
  for (let sq = 0; sq < MAX_SQUADS; sq++) if (world.sqUsed[sq]) used.push(sq);
  const picked = used.filter((sq) => (mask & (1 << sq)) !== 0);
  if (picked.length === 0 || picked.length === used.length) return used.length > 1 ? ALL_SQUADS_NAME : squadName(0);
  if (picked.length > 1) return `${picked.length}개 부대`;
  return squadName(picked[0]);
}

/**
 * 조이스틱 끈 거리를 8방향으로 자른다.
 *
 * 시뮬레이션은 -1, 0, 1만 받는다(`CmdType.MoveDir`). 손가락 각도를 그대로 넘길 수
 * 없으니 여기서 자른다. 한 축의 성분이 길이의 sin 22.5°(0.383)를 넘으면 그 축을
 * 켠다 — 그러면 여덟 방향이 45°씩 고르게 나뉜다. 가운데 25%는 멈춤이다. 손가락이
 * 떨리는 것만으로 장수가 걸으면 스스로 싸우던 장수가 계속 끊긴다.
 */
export function quantizeStick(dx: number, dy: number, radius: number): [number, number] {
  const len = Math.hypot(dx, dy);
  if (len < radius * 0.25) return [0, 0];
  const t = len * 0.383;
  return [Math.abs(dx) > t ? Math.sign(dx) : 0, Math.abs(dy) > t ? Math.sign(dy) : 0];
}

/**
 * 단추의 누름을 듣는다: 마우스와 키보드는 `click`, **손가락은 떼는 순간(`pointerup`)**.
 *
 * 손가락을 `click`으로 들으면 다른 손가락이 화면에 닿아 있는 동안에는 눌리지 않는다 — 브라우저는 한 손가락만 있을 때의
 * 톡을 `click`으로 친다. 조이스틱을 쥔 채(한 손가락으로 장수를 몰며) 다른 손가락으로 함성·명령을 누르는 것이 이 게임의
 * 기본 손짓인데 그때 단추가 먹지 않았다(리뷰·QA가 이벤트로 확인: 둘째 손가락의 pointerdown·pointerup은 오는데 click이
 * 없다 — 조이스틱이 붙박여 있던 때부터 그랬다). 손가락 하나로 누르면 `pointerup` 뒤에 `click`도 오므로, 손가락에서 나온
 * `click`은 버린다.
 */
const PRESS_SLOP = 16;

function onPress(el: HTMLElement, fn: () => void): void {
  let touched = -Infinity;
  el.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') return;
    // 손가락이 단추에서 멀리 나가서 뗐으면 누른 것이 아니다(손가락은 닿은 단추에 붙들려 밖에서 떼도 여기로 온다).
    // 테두리에서 `PRESS_SLOP`까지는 봐준다 — 엄지는 떼면서 구르고, 눌리는 자리를 넓힌 단추(함성)는 닿은 곳부터 밖이다.
    const r = el.getBoundingClientRect();
    if (e.clientX < r.left - PRESS_SLOP || e.clientX > r.right + PRESS_SLOP || e.clientY < r.top - PRESS_SLOP || e.clientY > r.bottom + PRESS_SLOP) return;
    touched = performance.now();
    fn();
  });
  el.addEventListener('click', (e) => {
    // 손가락에서 나온 `click`만 버린다(위에서 이미 받았다). 처음에는 "방금 손가락으로 눌렀으면 0.7초 동안의 click"을
    // 모두 버렸는데, 그러면 손가락으로 연 직후의 마우스 클릭과 키보드 Enter까지 먹지 않았다(재리뷰). `click`이 어디서
    // 왔는지는 `pointerType`이 말한다(키보드는 빈 글). 그것을 주지 않는 옛 브라우저에서만 시각으로 가른다.
    const from = (e as PointerEvent).pointerType;
    if (from === 'touch' || from === 'pen') return;
    if (from === undefined && performance.now() - touched < 700) return;
    fn();
  });
}

const ORDER_BUTTONS: readonly { order: Order; label: string; key: string }[] = [
  { order: Order.Hold, label: '대기', key: 'Z' },
  { order: Order.Formation, label: '대형', key: 'X' },
  { order: Order.Attack, label: '공격', key: 'C' },
  { order: Order.Retreat, label: '후퇴', key: 'V' },
];

/**
 * 부대 고르기가 게임·화면에 닿는 손잡이(`main.ts`가 루프와 렌더러로 채운다).
 *
 * 부대선택 버튼을 누르면 게임을 세운다. 목록에서 부대를 고르면 카메라가 그 지휘관에게 가고 부대원이 깜빡인다.
 * 부대를 고르면 곧장 게임이 다시 돌고, 조이스틱·휩쓸기가 그 부대의 지휘관(동료)을 몬다(`World.controlled`) — 내 부대·
 * 전 부대면 장수다. 플레이한 사람이 청했다(Todo8): "부대 선택 버튼으로 부대 하나를 선택하면 바로 그 부대장으로 포커스
 * 옮겨주고 해당 부대장을 조작하는 상태가 되도록 해줘." 전에는 고른 지휘관에서 끌어 경로를 그렸다(끌기 경로는 껐다).
 * 전에는 게임이 도는 중에 목록을 펼쳐 골랐다 — 난전 한가운데서 누가 어디 있는지 찾는 사이 판이 흘러갔다.
 */
export interface PickHooks {
  /** 게임을 세우거나 푼다(`GameLoop.hold`). */
  readonly hold: (on: boolean) => void;
  /** 고른 부대(마스크, 0이면 없음)를 화면에 알린다 — 카메라와 깜빡임(`Renderer.pickMask`). */
  readonly show: (mask: number) => void;
}

export class BattleControls {
  private readonly bar: HTMLElement;
  private readonly selectBtn: HTMLButtonElement;
  private readonly orderBtns: HTMLButtonElement[] = [];
  private readonly stick: HTMLElement;
  /** 장수의 함성 단추. 다시 외칠 수 있을 때까지 둘레가 차오른다(`World.roarCd`). */
  private readonly roarBtn: HTMLButtonElement;
  private roarShown = -1;
  private roarNone = false;
  private readonly knob: HTMLElement;
  private readonly input: InputState;
  private readonly world: World;
  /** 부대선택 드롭업. 부대선택 버튼 위로 펼친다. */
  private readonly menu: HTMLElement;
  /** document에 건 처리기를 한꺼번에 뗀다(`detach`). 판마다 쌓이면 지난 판 World까지 붙들고 남는다. */
  private readonly listening = new AbortController();
  /** 같은 내용이면 DOM을 다시 쓰지 않는다. 매 프레임 불린다. */
  private shown = '';
  private stickPointer: number | null = null;
  /** 전장에 닿은 손가락으로 조이스틱을 쥔다(`bindStick`이 만든다, `bindPick`이 전장에 건다). */
  private grab: (e: PointerEvent) => void = () => {};
  private pickHooks: PickHooks | null = null;
  /** 고르는 중인가(게임이 서 있다). */
  private picking = false;
  /** 고르는 중 목록에서 고른 부대. 0이면 아직 안 골랐다. */
  private pickedMask = 0;
  /** 고르는 동안 위에 띄우는 한 줄. */
  private readonly pickHint: HTMLElement;

  constructor(root: HTMLElement, input: InputState, world: World) {
    this.input = input;
    this.world = world;

    this.bar = document.createElement('div');
    this.bar.className = 'hud hud-cmdbar';

    this.selectBtn = document.createElement('button');
    this.selectBtn.className = 'cmd cmd-select';
    this.selectBtn.setAttribute('aria-haspopup', 'true');
    this.selectBtn.setAttribute('aria-expanded', 'false');
    onPress(this.selectBtn, () => this.toggleMenu());
    this.menu = document.createElement('div');
    this.menu.className = 'cmd-menu';
    this.menu.hidden = true;
    this.menu.setAttribute('role', 'menu');
    const pick = document.createElement('div');
    pick.className = 'cmd-pick';
    pick.append(this.selectBtn, this.menu);
    this.bar.append(pick);
    // 목록 밖을 누르면 접는다. 전장을 누르는 손가락(조이스틱·끌기)도 접는다 — 펼친 채 두면 명령 버튼을 가린다.
    const signal = this.listening.signal;
    document.addEventListener(
      'pointerdown',
      (e) => {
        const t = e.target as Node;
        if (pick.contains(t)) return;
        // 고르는 중이면 목록 밖 누르기가 고르기를 끝낸다. 전장은 입력(`InputState.picking`)이 따로 받는다 — 지휘관에서
        // 끌면 끝내지 않고 경로를 그려야 해서. 명령 단추는 제 click에서 명령을 싣고 끝낸다.
        if (this.picking) {
          if (this.stage?.contains(t) || this.bar.contains(t)) return;
          this.endPick();
          return;
        }
        if (!this.menu.hidden) this.closeMenu();
      },
      { signal },
    );
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        if (this.picking) this.endPick();
        else if (!this.menu.hidden) this.closeMenu();
      },
      { signal },
    );
    this.pickHint = document.createElement('div');
    this.pickHint.className = 'hud pick-hint';
    this.pickHint.hidden = true;
    this.pickHint.setAttribute('role', 'status');

    for (const b of ORDER_BUTTONS) {
      const btn = document.createElement('button');
      btn.className = `cmd cmd-order${b.order === Order.Retreat ? ' retreat' : ''}`;
      btn.innerHTML = `${b.label}<kbd>${b.key}</kbd>`;
      onPress(btn, () => {
        this.input.pressOrder(b.order);
        // 고르는 중에 명령을 누르면 그 부대에 싣고 게임을 다시 돌린다.
        if (this.picking) this.endPick();
      });
      this.orderBtns.push(btn);
      this.bar.append(btn);
    }

    this.stick = document.createElement('div');
    this.stick.className = 'hud-stick';
    this.knob = document.createElement('div');
    this.knob.className = 'knob';
    this.stick.append(this.knob);
    this.bindStick();

    // 눌러도 초점을 가져가지 않는다. 초점이 버튼에 남으면 다음에 누른 스페이스가
    // 휩쓸기 대신 그 버튼을 한 번 더 누른다.
    this.bar.addEventListener('pointerdown', (e) => e.preventDefault());

    this.roarBtn = document.createElement('button');
    this.roarBtn.className = 'hud-roar';
    // 단추는 들고 나온 장수 기술(Todo10, `sim/skills.ts`)의 이름이다. 자리는 예전 함성 단추 그대로.
    const skill = SKILLS.find((s) => SKILL_CODE[s.id] === this.world.skillCode) ?? SKILLS[0];
    // 걸 대상이 없을 때의 한 줄("궁수 없음")을 함께 둔다 — 폰에서는 제목(title)을 볼 수 없다. 네 글자 이름(일제사격)은
    // 둥근 단추에 빠듯해 글자를 줄인다(QA).
    this.roarBtn.innerHTML = `${skill.name}<small>${skill.id === 'volley' ? '궁수 없음' : '대상 없음'}</small><kbd>R</kbd>`;
    if (skill.name.length >= 4) this.roarBtn.classList.add('long');
    this.roarBtn.title = skill.summary(this.world.skillLevel);
    this.roarBtn.addEventListener('pointerdown', (e) => e.preventDefault());
    onPress(this.roarBtn, () => {
      // 대기 중에는 아무것도 하지 않는다. 막지 않으면 헛 명령이 로그에 실리고, 고르는 중이면 고르기를 버렸다.
      if (this.world.roarCd > 0 || this.world.skillTargets() === 0) return;
      this.input.pressRoar();
      // 고르는 중(서 있음)에 외치면 게임을 다시 돌린다 — 외침은 싸움 한가운데서 하는 것이다.
      if (this.picking) this.endPick();
    });

    root.append(this.bar, this.stick, this.roarBtn, this.pickHint);
  }

  /** 전장 요소. 고르는 중 전장 누르기는 입력이 받으므로 문서 처리기가 건너뛴다. */
  private stage: HTMLElement | null = null;

  /** 루프·렌더러가 생긴 뒤 `main.ts`가 부른다. 없으면 예전처럼 서지 않고 고른다. */
  bindPick(hooks: PickHooks, stage: HTMLElement): void {
    this.pickHooks = hooks;
    this.stage = stage;
    stage.addEventListener('pointerdown', (e) => this.grab(e), { signal: this.listening.signal });
    // 창이 가려지거나 초점을 잃으면 손을 뗀 것으로 본다. 붙든 손가락을 잃었다는 알림(`lostpointercapture`)에만 기대면,
    // 그 알림 없이 창이 넘어갔을 때 조이스틱이 쥔 채로 남아 장수가 계속 걷는다(리뷰가 흉내로 보였다).
    const drop = (): void => {
      if (this.stickPointer !== null) this.letGo();
    };
    window.addEventListener('blur', drop, { signal: this.listening.signal });
    document.addEventListener('visibilitychange', () => document.hidden && drop(), { signal: this.listening.signal });
  }

  /** 고르기를 시작한다: 게임을 세우고 목록을 편다. */
  private beginPick(): void {
    const h = this.pickHooks;
    if (!h || this.picking) return;
    this.picking = true;
    this.pickedMask = 0;
    h.hold(true);
    // 전장 누르기는 고르기를 끝낼 뿐이다(끌어 그리는 경로는 껐다).
    this.input.picking = { done: () => this.endPick() };
    this.pickHint.textContent = '일시정지 · 몰고 갈 부대장을 고른다';
    this.pickHint.hidden = false;
  }

  /** 고르기를 끝낸다: 게임을 다시 돌리고 목록·깜빡임을 거둔다. 고른 선택은 이미 입력에 실려 있다. */
  private endPick(): void {
    if (!this.picking) return;
    this.picking = false;
    this.pickedMask = 0;
    this.input.picking = null;
    this.pickHooks?.show(0);
    this.pickHooks?.hold(false);
    this.pickHint.hidden = true;
    this.closeMenu();
  }

  /** 목록에서 부대를 골랐다. 고르면 곧장 고르기를 끝내고 게임이 다시 돈다 — 그 부대장을 몰러 간다. */
  private choose(mask: number): void {
    this.input.pressSelect(mask);
    if (!this.picking) {
      this.closeMenu();
      return;
    }
    // 쓰는 부대만 남긴 꼴로 비교하고 적는다. "전 부대"는 부대 넷 모두(ALL_SQUADS)인데, 키보드 고르기를 맞추느라
    // 거른 꼴(쓰는 부대만)로 적어 두면서 원래 꼴과 비교해, 전 부대를 두 번 눌러도 같은 것으로 보지 않아 풀리지
    // 않았다(플레이한 사람이 짚었다).
    const m = normalizeSelect(mask, this.world.sqUsed);
    if (m === 0) return;
    // 고르면 곧장 다시 돈다 — 고른 부대장을 몰러 간다(카메라가 그 사람을 따라간다).
    this.endPick();
  }

  /** 부대선택 버튼: 목록을 펼치거나 접는다. 부대가 하나뿐이면 고를 것이 없다. 고르는 중이면 끝낸다. */
  private toggleMenu(): void {
    if (this.picking) {
      this.endPick();
      return;
    }
    if (!this.menu.hidden) {
      this.closeMenu();
      return;
    }
    const w = this.world;
    const choices = selectionChoices(w.sqUsed);
    if (choices.length <= 1) return;
    // 아직 틱에 실리지 않은 선택이 있으면 그것이 지금 선택이다.
    const current = this.input.pendingSelect ?? w.selectedMask;
    this.menu.replaceChildren(
      ...choices.map((mask) => {
        const b = document.createElement('button');
        b.className = 'cmd-choice';
        b.dataset.mask = String(mask);
        b.setAttribute('role', 'menuitemradio');
        const on = mask === current || (mask === ALL_SQUADS && isAll(current, w.sqUsed));
        b.setAttribute('aria-checked', String(on));
        if (mask !== ALL_SQUADS) {
          const sq = 31 - Math.clz32(mask);
          b.style.setProperty('--sq', SQUAD_COLORS[sq]);
          const pip = document.createElement('i');
          pip.className = 'pip';
          b.append(pip);
        }
        b.append(selectionName(mask, w));
        onPress(b, () => this.choose(mask));
        return b;
      }),
    );
    this.menu.hidden = false;
    this.selectBtn.setAttribute('aria-expanded', 'true');
    this.beginPick();
  }

  private closeMenu(): void {
    this.menu.hidden = true;
    this.selectBtn.setAttribute('aria-expanded', 'false');
  }

  private bindStick(): void {
    const center = (): { x: number; y: number; r: number } => {
      const rect = this.stick.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, r: rect.width / 2 };
    };
    const move = (e: PointerEvent): void => {
      const c = center();
      let dx = e.clientX - c.x;
      let dy = e.clientY - c.y;
      const [sx, sy] = quantizeStick(dx, dy, c.r);
      this.input.setStick(sx, sy, true);
      // 손잡이는 테두리 밖으로 나가지 않게 보여 준다. 판정은 이미 위에서 끝났다.
      const len = Math.hypot(dx, dy);
      if (len > c.r) {
        dx = (dx * c.r) / len;
        dy = (dy * c.r) / len;
      }
      this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
    };
    const release = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return;
      this.letGo();
    };
    // 조이스틱은 정해진 자리가 없다: 전장의 아무 데나 손가락을 대면 그 자리가 가운데가 된다(`grab`). 처음에는 오른쪽
    // 아래에 붙박여 있었는데, 그 옆의 함성 단추와 함께 장수를 가렸고 엄지를 늘 그 자리에 둬야 했다(플레이한 사람이 바꿨다).
    this.grab = (e: PointerEvent): void => {
      // 마우스는 쥐지 않는다 — 끄는 것은 WASD보다 불편하기만 하다. 조작이 멎었거나(장수가 쓰러졌다) 이미 쥐고 있으면 그만.
      if (e.pointerType === 'mouse' || this.stickPointer !== null || this.stick.classList.contains('off')) return;
      // 고르는 중 전장 누르기는 고르기를 끝낸다(입력이 받는다) — 그 손가락으로 조이스틱까지 쥐지 않는다. 입력이 먼저
      // 듣고 고르기를 끝내므로 여기 올 때는 이미 고르는 중이 아니다: 입력이 먹은 누름(`preventDefault`)인지로 가른다.
      // 처음에는 `picking`만 봐서, 고르기를 닫으려던 누름이 그대로 장수를 몰았다(리뷰·QA).
      if (this.picking || e.defaultPrevented) return;
      const base = (this.stick.offsetParent ?? this.stick.parentElement)?.getBoundingClientRect();
      this.stick.style.left = `${e.clientX - (base?.left ?? 0)}px`;
      this.stick.style.top = `${e.clientY - (base?.top ?? 0)}px`;
      this.stick.classList.add('on');
      this.stickPointer = e.pointerId;
      // 손가락은 전장에 닿았지만 그 뒤의 움직임은 조이스틱이 받는다.
      try {
        this.stick.setPointerCapture(e.pointerId);
      } catch {
        // 이미 끝난 손가락이다.
        this.letGo();
        return;
      }
      move(e);
      e.preventDefault();
    };
    this.stick.addEventListener('pointermove', (e) => {
      if (e.pointerId === this.stickPointer) move(e);
    });
    this.stick.addEventListener('pointerup', release);
    this.stick.addEventListener('pointercancel', release);
    // 캡처를 다른 이유로 잃어도(탭 전환, 시스템 제스처) 손을 뗀 것으로 본다.
    // 아니면 stickPointer가 남아 다음 손가락을 계속 거절한다.
    this.stick.addEventListener('lostpointercapture', release);
  }

  /** 조이스틱에서 손을 뗀다. 어느 길로 오든 여기 하나로 모인다. */
  private letGo(): void {
    const id = this.stickPointer;
    this.stickPointer = null;
    if (id !== null && this.stick.hasPointerCapture(id)) this.stick.releasePointerCapture(id);
    this.input.setStick(0, 0, false);
    this.knob.style.transform = '';
    this.stick.classList.remove('on');
  }

  update(): void {
    // 고르는 중에 키보드로 고르거나(숫자키) 명령하면(ZXCV) 단추와 같게 다룬다. 전에는 숫자키가 고르기를 건너뛰어,
    // 깜빡이는 부대와 실제로 고른 부대가 달라지고 경로가 엉뚱한 부대에 갔다. ZXCV는 명령만 싣고 게임을 세운 채
    // 두었다(리뷰가 짚었다).
    if (this.picking) {
      const sel = this.input.pendingSelect;
      // 쓰지 않는 부대의 숫자키는 틱에서 걸러진다(`normalizeSelect`). 여기서도 걸러 없는 부대를 안내하지 않는다.
      const real = sel === null ? 0 : normalizeSelect(sel, this.world.sqUsed);
      if (real !== 0 && real !== this.pickedMask) this.choose(real);
      // 대기 중의 R은 버린다(단추와 같게). 헛 명령을 위해 고르기를 끝내면 안 된다.
      if (this.input.roar && (this.world.roarCd > 0 || this.world.skillTargets() === 0)) this.input.roar = false;
      if (this.input.pendingOrder !== null || this.input.roar) this.endPick();
    }
    const w = this.world;
    // 함성 단추: 남은 대기를 둘레로 보인다. 틱 단위라 값이 바뀔 때만 쓴다.
    if (w.roarCd !== this.roarShown) {
      this.roarShown = w.roarCd;
      this.roarBtn.style.setProperty('--p', String(1 - w.roarCd / w.skillCdMax));
      this.roarBtn.classList.toggle('ready', w.roarCd === 0);
    }
    // 걸 대상이 없으면(궁수 없는 분대를 고른 일제사격) 흐리게 하고 그렇다고 적는다. 고른 분대가 바뀌면 달라지므로 매번 본다
    // — 함성은 늘 걸리므로 세지 않는다(`skillTargets`).
    const none = w.skillTargets() === 0;
    if (none !== this.roarNone) {
      this.roarNone = none;
      this.roarBtn.classList.toggle('none', none);
    }
    this.roarBtn.setAttribute('aria-disabled', String(w.roarCd > 0 || none));
    const mask = this.input.pendingSelect ?? w.selectedMask;
    let multi = 0;
    for (let sq = 0; sq < MAX_SQUADS; sq++) if (w.sqUsed[sq]) multi++;
    // 고른 부대들이 모두 같은 명령이면 그 버튼에 불을 켠다. 섞였으면 아무것도 켜지 않는다.
    let common = -1;
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      if (!w.sqUsed[sq] || (mask & (1 << sq)) === 0) continue;
      const o = w.sqOrder[sq];
      common = common === -1 || common === o ? o : -2;
    }
    const key = `${mask}|${multi}|${common}|${w.sqCommander.join(',')}`;
    if (key === this.shown) return;
    this.shown = key;

    // ▴: 누르면 위로 목록이 펼쳐진다는 표시. 부대가 하나뿐이면 고를 것이 없어 붙이지 않는다.
    this.selectBtn.textContent = selectionName(mask, w) + (multi > 1 ? ' ▴' : '');
    // 부대가 하나뿐이면 고를 것이 없다. 버튼은 남겨 두되 눌러도 펼쳐지지 않는다는 것을 보인다.
    this.selectBtn.classList.toggle('single', multi <= 1);
    ORDER_BUTTONS.forEach((b, k) => this.orderBtns[k].classList.toggle('on', b.order === common));
  }

  /**
   * 마우스 화면에서는 함성 단추를 장수 체력 줄 옆(`dock`)에 둔다. 플레이한 사람이 청했다: "PC에서 함성 버튼을 장수 HP
   * section 옆으로 옮겨줘." 오른쪽 아래 구석은 눈이 가지 않는다 — 싸움을 보며 늘 보는 곳이 장수 체력이다. 손가락 기기와
   * 좁은 창은 예전 자리(조이스틱 왼쪽, 명령 줄 위) 그대로다: 체력 줄이 화면 위로 올라가 손이 닿지 않는다.
   */
  dockRoar(dock: HTMLElement): void {
    const wide = window.matchMedia('(pointer: fine) and (min-width: 761px)');
    const home = this.roarBtn.parentElement;
    const place = (): void => {
      if (wide.matches) dock.append(this.roarBtn);
      else if (home && this.roarBtn.parentElement !== home) home.append(this.roarBtn);
    };
    place();
    wide.addEventListener('change', place, { signal: this.listening.signal });
  }

  /** 전장을 닫을 때. document에 건 처리기를 뗀다. */
  detach(): void {
    this.listening.abort();
  }

  /** 전투가 끝나면 손가락 입력을 거둔다. 결과 패널을 눌러야 한다. */
  suspend(): void {
    this.endPick();
    this.roarBtn.classList.add('off');
    this.closeMenu();
    this.bar.classList.add('off');
    this.stick.classList.add('off');
    this.letGo();
  }
}
